import { useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseCard from "../../../components/ui/BaseCard";
import BaseTextField from "../../../components/ui/BaseTextField";
import { useMutation } from "@tanstack/react-query";
import { inviteEngineer } from "../../../services/api/admin";
import * as Yup from "yup";

const InviteEngineerModal = ({
  isInviteEngineerModalOpen,
  setIsInviteEngineerModalOpen,
  onInvited,
}) => {
  const [email, setEmail] = useState("");
  const [validationError, setValidationError] = useState("");

  const emailSchema = Yup.string()
    .required("Email is required")
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format");

  const {
    isPending: isInviteEngineerApiLoading,
    error: inviteEngineerApiError,
    mutateAsync: inviteEngineerMutation,
  } = useMutation({
    mutationFn: (email) => inviteEngineer(email),
  });

  const handleConfirmInviteButton = async () => {
    try {
      await emailSchema.validate(email);
      setValidationError("");
      await inviteEngineerMutation(email);
      onInvited?.();
      setIsInviteEngineerModalOpen(false);
    } catch (error) {
      if (error.name === "ValidationError") {
        setValidationError(error.message);
      } else {
        console.error("Error inviting engineer:", error);
      }
    }
  };

  return (
    <BaseModal open={isInviteEngineerModalOpen} close={() => setIsInviteEngineerModalOpen(false)}>
      <div className="container bg-[#F6F6F6] py-7 xl:w-[800px] lg:w-[900px] sm:w-[700px]">
        <h6
          className="text-dark border-bottom font-normal"
          style={{ marginBottom: "24px", paddingBottom: "4px" }}
        >
          Invite Engineer
        </h6>

        <BaseCard className="p-5 space-y-4">
          <p className="text-base text-primary mb-3">An invitation email will be sent to:</p>

          <BaseTextField
            placeholder="Enter email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            startAdornment={<i className="fa-solid fa-envelope text-lg text-gray-500"></i>}
          />
          {/* ✅ Validation + API errors */}
          {validationError && <p className="text-red-500 text-sm">{validationError}</p>}
          {inviteEngineerApiError && (
            <p className="text-red-500 text-sm">
              {inviteEngineerApiError.message || "Failed to send invitation"}
            </p>
          )}
        </BaseCard>
      </div>

      <div className="xl:flex grid grid-cols-2 gap-4 xl:justify-center bg-white py-6 px-4">
        <div className="xl:block col-span-2">
          <BaseButton
            className="py-[6px] px-[40px] bg-light text-dark"
            fullWidth={true}
            onClick={() => setIsInviteEngineerModalOpen(false)}
          >
            Cancel
          </BaseButton>
        </div>
        <div className="xl:block col-span-2">
          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            disabled={isInviteEngineerApiLoading}
            isLoading={isInviteEngineerApiLoading}
            className="py-[6px] px-[40px]"
            color="primary"
            fullWidth={true}
            onClick={handleConfirmInviteButton}
          >
            Confirm
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default InviteEngineerModal;
